import { useAuth } from 'providers/auth-provider'
import React, { ReactNode, useEffect, useMemo } from 'react'
import { LoadingPage } from './pages/loading'
import { OutOfCredits } from './pages/out-of-credits'
import { UnknownError } from './pages/unknown-error'
import { useNavigate, useParams } from '@tanstack/react-router'
import i18n from 'utils/i18n/i18n'
import { helmet } from 'helmet'
import { Helmet } from 'react-helmet-async'
import {
  InterviewGetVacancyFallbackProps,
  InterviewGetInterviewIdFallbackProps,
  InterviewRootFallbackProps,
  InterviewGetVacancyUiSettingsFallbackProps
} from './interview-ui.types'
import { useUnit } from 'effector-react'
import { useVacancy } from 'hooks/use-vacancy'
import { interviewFixturesService } from './interview-fixtures.service'
import { InterviewStageEnum } from 'consts/company-alias.consts'
import { Route, QueryParams } from 'routes/$companyAlias/$positionAlias'
import { interviewIdSaveService } from './pages/call/interview-id-save.service'
import { useGetVacancyUiSettingsApiV1VacanciesVacancyIdUiSettingsGet } from '../../api/endpoints/openapi-generated'
import { VacancyDefaultUISettings } from '../../api/endpoints/openapi-generated.schemas'

type Params = {
  positionAlias?: string
}

function useVacancyId(): string | null {
  const navigate = useNavigate()
  const { positionAlias: vacancyIdFromParams } = useParams({
    strict: false
  }) as Params
  const vacancyId = useUnit(interviewFixturesService.$vacancyId)

  useEffect(() => {
    interviewFixturesService.setVacancyId(vacancyIdFromParams ?? null)

    if (!vacancyIdFromParams) {
      void navigate({ to: '/' })
    }

    return () => {
      interviewFixturesService.setVacancyId(null)
    }
  }, [vacancyIdFromParams, navigate])

  if (vacancyId !== vacancyIdFromParams) {
    return null
  }

  return vacancyId
}

export function InterviewRootFallback(props: InterviewRootFallbackProps) {
  const { user } = useAuth()
  const vacancyId = useVacancyId()

  useEffect((): VoidFunction => {
    return interviewFixturesService.subscribe()
  }, [])

  if (!vacancyId || !user) {
    return <LoadingPage />
  }

  return (
    <InterviewVacancyFallback {...props} vacancyId={vacancyId} user={user} />
  )
}

export function InterviewVacancyFallback(
  props: InterviewGetVacancyFallbackProps
): ReactNode {
  const { vacancyId } = props

  useEffect(() => {
    return interviewIdSaveService.subscribe()
  }, [])

  const { data, isLoading, error } = useVacancy(vacancyId)

  const vacancy = data?.data ?? null

  if (isLoading || (!vacancy && !error)) {
    return <LoadingPage />
  }

  if (error || !vacancy) {
    const isForbiddenError = error?.status === 403

    if (isForbiddenError) {
      return <OutOfCredits />
    }

    return <UnknownError />
  }

  return (
    <>
      <Helmet helmetData={helmet}>
        <title>
          {vacancy.title} at {vacancy.company.name}
        </title>
      </Helmet>

      <InterviewVacancyUiSettingsFallback {...props} vacancy={vacancy} />
    </>
  )
}

function InterviewVacancyUiSettingsFallback(
  props: InterviewGetVacancyUiSettingsFallbackProps
): ReactNode {
  const { vacancy } = props
  const { data, isLoading, error } =
    useGetVacancyUiSettingsApiV1VacanciesVacancyIdUiSettingsGet(vacancy.id)

  const uiSettings = useMemo(():
    | VacancyDefaultUISettings
    | null
    | undefined => {
    if (!data) return

    const loadedSettings = data.data.settings.filter(
      (ui) => ui.type === 'default'
    )

    if (!loadedSettings.length) {
      console.log('ui settings for vacancy not found')
      return null
    }

    return loadedSettings[0]
  }, [data])

  if (isLoading || (uiSettings === void 0 && !error)) {
    return <LoadingPage />
  }

  if (error || !uiSettings) {
    return <UnknownError />
  }

  return (
    <InterviewGetInterviewIdFallback
      {...props}
      vacancyUiSettings={uiSettings}
    />
  )
}

function InterviewGetInterviewIdFallback(
  props: InterviewGetInterviewIdFallbackProps
): ReactNode {
  const { vacancy } = props
  const { stage } = Route.useSearch() satisfies QueryParams
  const navigate = useNavigate()
  const { refetchUser } = useAuth()
  const interviewId = useUnit(interviewFixturesService.$interviewId)

  useEffect(() => {
    void i18n.changeLanguage(vacancy.language ?? vacancy.interviewer?.language)
  }, [vacancy])

  const isSearchParamsWelcome = stage === InterviewStageEnum.Welcome
  const canRender = !!interviewId || isSearchParamsWelcome

  useEffect(() => {
    if (canRender) return

    void navigate({
      to: '/$companyAlias/$positionAlias',
      params: {
        companyAlias: 'interview',
        positionAlias: vacancy.id
      },
      search: {
        stage: InterviewStageEnum.Welcome
      }
    })
  }, [canRender, navigate, vacancy.id])

  useEffect(() => {
    if (!interviewId) return
    void refetchUser()
  }, [interviewId, refetchUser])

  if (!canRender) {
    return <LoadingPage />
  }

  return (
    <>
      {props.children({
        ...props,
        interviewId,
        isSearchParamsWelcome
      })}
    </>
  )
}
